import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { QrCode, ChevronLeft, AlertCircle } from "lucide-react";
import QRCode from "qrcode.react";

const QR = () => {
    const navigate = useNavigate();
    const { user } = useUserAuth();
    const [balance, setBalance] = useState(1250); // Valor por defecto
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const fetchUserBalance = async () => {
            if (!user) {
                setLoading(false);
                return;
            }
            try {
                const firestore = getFirestore();
                const docRef = doc(firestore, "Users", user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const userData = docSnap.data();
                    setBalance(userData.balance || 0); // Usa 0 si balance no existe
                } else {
                    setBalance(0); // Si no existe el documento, balance es 0
                }
            } catch (error) {
                console.error("Error fetching user balance:", error);
                setErrorMessage("Error al cargar el balance de PolloCoins.");
            } finally {
                setLoading(false);
            }
        };
        fetchUserBalance();
    }, [user]);

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FFBC00] px-4">
                <div className="bg-[#F5EBDD] border-2 border-[#FF0000] p-4 sm:p-6 rounded-none shadow-xl max-w-md w-full text-center">
                    <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-[#FF0000]" />
                    <h2 className="text-lg sm:text-xl font-bold text-[#FF0000] uppercase tracking-wide mb-2">
                        Acceso Restringido
                    </h2>
                    <p className="text-[#000000] text-sm sm:text-base mb-4 sm:mb-6">
                        Debes iniciar sesión para ver tu código QR
                    </p>
                    <button
                        onClick={() => navigate("/login")}
                        className="w-full bg-[#FF0000] text-[#F5EBDD] font-bold uppercase tracking-wide py-2 sm:py-3 px-4 rounded-none hover:bg-[#000000] hover:text-[#FFBC00] transition-all active:scale-95"
                    >
                        Iniciar Sesión
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FFBC00] px-4">
                <div className="flex flex-col items-center gap-4 text-center">
                    <QrCode className="w-12 h-12 animate-pulse text-[#FF0000]" />
                    <p className="text-[#000000] font-bold uppercase tracking-wide text-sm sm:text-base">
                        Cargando PolloCoins...
                    </p>
                </div>
            </div>
        );
    }

    const estimatedValue = (balance * 0.1).toFixed(2); // Tasa de conversión: 0.1 MXN por PolloCoin

    return (
        <div className="min-h-screen bg-[#FFBC00]">
            <div className="max-w-md mx-auto px-4 sm:px-6 md:px-8 pt-4 sm:pt-6">
                <div className="sticky top-0 bg-[#F5EBDD] z-10 py-3 sm:py-4 mb-4 sm:mb-6 flex items-center justify-between border-b-2 border-[#FF0000] shadow-sm rounded-none">
                    <button
                        onClick={() => navigate("/settings")}
                        className="p-2 hover:bg-[#FFF8E1] rounded-none transition-all active:scale-95"
                        aria-label="Volver"
                    >
                        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF0000]" />
                    </button>
                    <h1 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide">
                        PolloCoins
                    </h1>
                    <div className="w-5 h-5 sm:w-6 sm:h-6"></div> {/* Placeholder para alineación */}
                </div>
                {errorMessage && (
                    <div className="bg-[#FF0000] border-2 border-[#FF0000] p-3 rounded-none flex items-start max-w-xs sm:max-w-sm mx-auto mb-4 sm:mb-6">
                        <AlertCircle className="h-5 w-5 text-[#F5EBDD] mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-bold text-[#F5EBDD] text-sm uppercase tracking-wide">Error</p>
                            <p className="text-xs text-[#F5EBDD]">{errorMessage}</p>
                        </div>
                    </div>
                )}
                <div className="bg-[#F5EBDD] border-2 border-[#FF0000] rounded-none shadow-xl mb-6 p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-bold text-[#FF0000] uppercase tracking-wide mb-4">
                        Tu Código QR
                    </h2>
                    <div className="flex justify-center mb-4 sm:mb-6 p-4 bg-white border border-[#FF0000] w-[180px] sm:w-[200px] mx-auto">
                        <QRCode value={user.uid} size={160} bgColor="#FFFFFF" fgColor="#000000" />
                    </div>
                    <p className="text-xs sm:text-sm text-[#000000] text-center mb-4 sm:mb-6">
                        Escanea este código para identificarte
                    </p>
                    <div className="border-t-2 border-[#FF0000] pt-4">
                        <h3 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide mb-2">
                            Balance Actual
                        </h3>
                        <p className="text-xl sm:text-2xl font-bold text-[#000000] mb-2">
                            {balance.toLocaleString()} PolloCoins
                        </p>
                        <p className="text-sm sm:text-base text-[#FF0000]">
                            Valor Estimado: ≈${estimatedValue} MXN
                        </p>
                    </div>
                </div>
                <div className="bg-[#F5EBDD] border-2 border-[#FF0000] rounded-none shadow-xl mb-6 p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide mb-4">
                        ¿Qué son los PolloCoins?
                    </h3>
                    <p className="text-xs sm:text-sm text-[#000000] mb-4">
                        Son puntos que acumulas y puedes usar para obtener beneficios.
                    </p>
                    <h4 className="text-sm sm:text-base font-bold text-[#FF0000] uppercase tracking-wide mb-2">
                        Instrucciones de Uso
                    </h4>
                    <ol className="list-decimal pl-5 text-xs sm:text-sm text-[#000000] space-y-2">
                        <li>
                            <span className="font-bold">Escanea tu QR:</span> Muestra tu código QR al personal de la sucursal al realizar tu pedido.
                        </li>
                        <li>
                            <span className="font-bold">Acumula PolloCoins:</span> Gana puntos por cada compra que realices.
                        </li>
                        <li>
                            <span className="font-bold">Canjea Recompensas:</span> Usa tus puntos acumulados para obtener descuentos y premios.
                        </li>
                    </ol>
                </div>
                <div className="bg-[#F5EBDD] border-2 border-[#FF0000] rounded-none shadow-xl mb-6 p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide mb-4">
                        ¿Qué puedes hacer con tu QR?
                    </h3>
                    <ul className="list-disc pl-5 text-xs sm:text-sm text-[#000000] space-y-2">
                        <li>
                            <span className="font-bold">Escanear en sucursal:</span> Identificarte al ordenar.
                        </li>
                        <li>
                            <span className="font-bold">Acumular PolloCoins:</span> Ganar puntos por tus compras.
                        </li>
                        <li>
                            <span className="font-bold">Canjear recompensas:</span> Usar tus puntos por premios.
                        </li>
                        <li>
                            <span className="font-bold">Recoger pedidos:</span> Mostrar el QR para retirar pedidos realizados.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default QR;